import {Range, Map} from 'immutable';
import type {List} from 'immutable';
import type {QuestionModelV3} from '@albert-io/models';

/**
 * This file is a port from the old `passageCorrectionQuestionExtensions`.
 * Instead of adding the methods to the question type these are now just functions
 * that take in the question to reduce noise on the question model.
 */

export function getUncorrectedText(question: QuestionModelV3): string {
  return question.get('uncorrected_text');
}

export function getRegionIndices(question: QuestionModelV3): Map<string, Map<string, number>> {
  return question.get('region_indices', Map());
}

export function getEnumeratedRegionIndices(question: QuestionModelV3): Map<number, boolean> {
  const func = () =>
    getRegionIndices(question).reduce(
      (acc?: Map<number, boolean>, region?: Map<string, number>) => {
        const startIndex = region!.get('start_index');
        const endIndex = region!.get('end_index');
        const indicesInRegion = Range(startIndex, endIndex! + 1).reduce(
          (innerAcc?: Map<number, boolean>, val?: number) => {
            return innerAcc!.set(val!, true);
          },
          Map<number, boolean>()
        );
        return acc!.merge(indicesInRegion);
      },
      Map<number, boolean>()
    );

  return question.cache('getEnumeratedRegionIndices', func);
}

export function isOptionIndexInteractive(
  question: QuestionModelV3,
  optionIndex: number
): boolean {
  return getEnumeratedRegionIndices(question).get(parseInt(String(optionIndex), 10)) === true;
}

export function getRegionForOptionIndex(
  question: QuestionModelV3,
  optionIndex: number
): Map<string, any> {
  const matchedRegion = getRegionKeyForOptionIndex(question, optionIndex);
  return question
    .getMeta()
    .getQuestionRubric()
    .get('validResponse')
    .get(matchedRegion);
}

export function getRegionKeyForOptionIndex(
  question: QuestionModelV3,
  optionIndex: number
): Map<string, any> {
  const func = () =>
    getRegionIndices(question).findKey((region?: Map<string, number>) => {
      return (
        [region!.get('start_index'), optionIndex, region!.get('end_index')].sort(
          (a, b) => a! - b!
        )[1] === optionIndex
      );
    });
  return question.cache(`getRegionKeyForOptionIndex/${optionIndex}`, func);
}

export function getSolutionsForRegionKey(
  question: QuestionModelV3,
  regionKey: string
): List<string> {
  return question.getValidResponse().getIn([regionKey, 'solutions']);
}

export function isRegionDistractor(question: QuestionModelV3, regionKey: string): boolean {
  return getSolutionsForRegionKey(question, regionKey) === null;
}

export function doesRegionContainOptionIndex(
  region: Map<string, any>,
  optionIndex: number
): boolean {
  const matchedStartIndex = region.get('start_index');
  const matchedEndIndex = region.get('end_index');
  return matchedStartIndex <= optionIndex && optionIndex <= matchedEndIndex;
}
